import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#1A3A5C]">
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
          transform: `translateY(${scrollY * 0.3}px)`
        }}></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/30 rounded-full animate-float"
            style={{
              left: `${(i * 17 + 5) % 100}%`,
              top: `${(i * 13 + 7) % 100}%`,
              animationDelay: `${(i * 0.3) % 5}s`,
              animationDuration: `${5 + (i % 10)}s`
            }}
          ></div>
        ))}
      </div>

      {/* Background Logo Watermark */}
      <div className="absolute inset-0 flex items-center justify-center opacity-5">
        <img
          src="https://static.readdy.ai/image/66a2b9cfb9b417b2846085a20d5b8a97/39f48c36d78167ba77a6bec1ef6513fc.png"
          alt=""
          className="w-[800px] h-[800px] object-contain"
          style={{ transform: `scale(${1 + scrollY * 0.0005})` }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
        {/* Pulsing Glow Effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse"></div>

        <div className="relative">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm mb-8 border border-white/20 animate-fade-in">
            <i className="ri-shield-check-line text-primary"></i>
            <span>{t('hero_badge')}</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-6xl md:text-7xl font-black text-white mb-6 leading-tight animate-fade-in-up">
            {t('hero_title_line1')}
            <br />
            <span className="bg-gradient-to-r from-primary via-[#00D9FF] to-primary bg-clip-text text-transparent animate-gradient">
              {t('hero_title_line2')}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-white/80 mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            {t('hero_subtitle')}
            <br />
            <span className="text-white/60 text-lg">{t('hero_subtitle_tags')}</span>
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <Link
              to="/register-nft"
              className="group px-8 py-4 bg-gradient-primary text-white rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-primary/50 transition-all hover:scale-105 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-file-add-line text-xl"></i>
              {t('hero_btn_register')}
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
            </Link>
            <Link
              to="/verify"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-bold text-lg border-2 border-white/30 hover:bg-white/20 hover:border-white/50 transition-all hover:scale-105 cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-search-line text-xl"></i>
              {t('hero_btn_verify')}
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mt-16 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">10K+</div>
              <div className="text-white/60 text-sm">{t('hero_stat_registered')}</div>
            </div>
            <div className="text-center border-x border-white/20">
              <div className="text-4xl font-bold text-white mb-2">99.9%</div>
              <div className="text-white/60 text-sm">{t('hero_stat_security')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-white mb-2">24/7</div>
              <div className="text-white/60 text-sm">{t('hero_stat_verify')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-3xl text-white/50"></i>
      </div>
    </section>
  );
}
